import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RowSelectionState } from "@tanstack/react-table";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { AccessControlModal } from "components/modals/access-control-modal";
import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipButton,
  TooltipWrapper,
} from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import CustomTable from "components/shared/table";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "components/ui/alert";
import { Button } from "components/ui/button";
import { Card } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Skeleton } from "components/ui/skeleton";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { deleteBatchJobs } from "data/mutations-common";
import { fetchBatches } from "data/queries-common";
import batchDark from "icons/batch-dark.png";
import batchLight from "icons/batch-light.png";
import { Bot, Plus } from "lucide-react";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import { v4 as uuidv4 } from "uuid";
import { buildColumns } from "./columns-row";
import { BatchConfig } from "./models";
import {
  batchPermissionKeys,
  BatchRow,
  isPermittedLegacy,
  premiumBatchPermissionKeys,
  ProcessBatchRow,
  SelectedNamesList,
} from "./utils";
import { PageTitle } from "@/components/page-title";

export const BatchPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const {
    isPermitted,
    permissionDeniedMessageString,
    isPermissionsSuccess,
    isPermissionsLoading,
  } = usePermissions();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const [searchQuery, setSearchQuery] = useState("");

  const batchesQuery = useQuery({
    queryKey: [QueryKeys.batchesKey, identityId],
    queryFn: () => fetchBatches(),
    refetchInterval: 5000,
  });

  const isLoading = batchesQuery.isPending || isPermissionsLoading;
  const isSuccess = batchesQuery.isSuccess && isPermissionsSuccess;

  const batchJobDeleteMutation = useMutation({
    mutationFn: () => deleteBatchJobs(selectedIds),
    onSuccess: async () => {
      await batchesQuery.refetch();
      setDeleteModalIsOpen(false);
    },
  });

  const rows: BatchRow[] = batchesQuery.isSuccess
    ? batchesQuery.data
        .map((batchConfig: BatchConfig) => ProcessBatchRow(batchConfig))
        .filter((batchConfig: ReturnType<typeof ProcessBatchRow>) => {
          return (
            batchConfig.displayName.toLowerCase().includes(searchQuery) ||
            batchConfig.id.toLowerCase().includes(searchQuery)
          );
        })
    : [];

  const hasPremiumJobHistory = rows
    .map((row: BatchRow) => row.isPremium)
    .some(Boolean);

  const columns = buildColumns({
    batchesQuery,
    identityId,
    identityState,
    queryClient,
    onDeleteRow: (row) => {
      setSelectedIds([row.id]);
      setSelectedNames([row.displayName]);
      setDeleteModalIsOpen(true);
    },
  });

  const NoSubNoHistoryContent = () => {
    return (
      <div>
        <ul className="list-disc pl-6 space-y-1">
          <li className="text-sm">
            Validate, geocode, and enrich your addresses or
          </li>
          <li className="text-sm">Reverse geocode locations to addresses</li>
        </ul>

        <p className="text-sm mt-2">
          Using G-NAF and G-NAF Live, Australia's most up-to-date national
          address source.
        </p>

        <p className="text-sm mt-2">
          Batch Premium can additionally return insights on:
        </p>

        <ul className="list-disc pl-10 space-y-1">
          <li className="text-sm">Solar</li>
          <li className="text-sm">Property</li>
          <li className="text-sm">And more</li>
        </ul>

        <p className="text-sm mt-2">Subscribe to get started with Batch</p>
      </div>
    );
  };

  const batchContent = () => {
    return (
      <div className="flex flex-col gap-4">
        <ReactQueryErrorWrapper
          queries={[batchesQuery]}
          mutations={[batchJobDeleteMutation]}
        />
        <PageTitle
          icon={Bot}
          title={"Batch"}
          description={
            "Geocode or reverse geocode large address lists by uploading a file"
          }
          actions={
            <PermissionTooltipButton
              permissionKeys={[PermissionsKeys.batchCreate]}
              onClick={() =>
                history.push(Routes.batchCreateBatch, { batchId: uuidv4() })
              }
            >
              <Plus />
              New Job
            </PermissionTooltipButton>
          }
        />

        {/* Display alert if user does not have premium */}
        <PermissionsWrapper
          keys={premiumBatchPermissionKeys}
          messages={{
            [PermissionsReasons.ROLE_DENY]: <></>,
            [PermissionsReasons.UPGRADE_REQUIRED]: (
              <Alert variant="info">
                <AlertTitle>Upgrade for premium</AlertTitle>
                <AlertDescription>
                  Want more from Batch? Upgrade to a paid plan for larger credit
                  balances and access to premium data such as Solar, Property,
                  and Parcel/Cadastre.
                </AlertDescription>
                <AlertAction>
                  <UpgradeSubscriptionButton />
                </AlertAction>
              </Alert>
            ),
          }}
        >
          <></>
        </PermissionsWrapper>

        {/* Display alert if user does not have premium but they do have premium jobs */}
        {hasPremiumJobHistory && (
          <PermissionsWrapper
            keys={premiumBatchPermissionKeys}
            messages={{
              [PermissionsReasons.ROLE_DENY]: <></>,
              [PermissionsReasons.UPGRADE_REQUIRED]: (
                <Alert variant="info">
                  <AlertTitle>Premium Batch jobs unavailable</AlertTitle>
                  <AlertDescription>
                    Looks like some of your batch jobs use premium data. To
                    access them again or create new ones with premium data,
                    please upgrade to a paid plan.
                  </AlertDescription>
                  <AlertAction>
                    <UpgradeSubscriptionButton />
                  </AlertAction>
                </Alert>
              ),
            }}
          >
            <></>
          </PermissionsWrapper>
        )}

        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2">
            <Input
              id="batch-search"
              placeholder="Search batch jobs..."
              onChange={(event) =>
                setSearchQuery(event.target.value.toLowerCase())
              }
              value={searchQuery}
            />

            <TooltipWrapper
              title={permissionDeniedMessageString([
                PermissionsKeys.batchDelete,
              ])}
            >
              <Button
                variant="destructive"
                onClick={() => {
                  setDeleteModalIsOpen(true);
                }}
                disabled={selectedIds.length === 0}
              >
                Delete Job
              </Button>
            </TooltipWrapper>

            <ConfirmationModal
              isLoading={
                batchJobDeleteMutation.isPending || batchesQuery.isPending
              }
              isError={batchJobDeleteMutation.isError}
              open={deleteModalIsOpen}
              header={`Delete ${selectedIds.length} job${selectedIds.length === 1 ? "" : "s"}?`}
              body={`Are you sure you want to completely delete ${selectedIds.length} jobs?`}
              body2={
                "This will delete the input and output files along with all related batch job information."
              }
              leftButtonText={"CANCEL"}
              leftButtonColor="primary"
              rightButtonText={"DELETE JOBS"}
              rightButtonColor="error"
              handleLeftButton={() => setDeleteModalIsOpen(false)}
              handleRightButton={() => batchJobDeleteMutation.mutate()}
              handleClose={() => setDeleteModalIsOpen(false)}
              type={"Batch"}
              children={<SelectedNamesList selectedNames={selectedNames} />}
            />
          </div>

          <CustomTable
            rows={rows}
            columns={columns}
            checkboxSelection={true}
            onRowSelectionChange={(selection: RowSelectionState) => {
              const ids = Object.keys(selection);
              setSelectedIds(ids);

              const selectedRowsDataNames = ids.map(
                (selectedRowId) =>
                  rows.find((row) => row.id === selectedRowId)!.displayName,
              );
              setSelectedNames(selectedRowsDataNames);
            }}
            isRowSelectable={(row) => {
              if (row.status === "Running") {
                return false;
              }

              if (
                isPermittedLegacy(row.subscriptionProductKey ?? "", row.status)
              ) {
                return true;
              }

              if (!isPermitted([PermissionsKeys.batchDelete])) {
                return false;
              }

              if (
                row.isPremium &&
                !isPermitted([PermissionsKeys.premiumDataDelete])
              ) {
                return false;
              }

              return true;
            }}
            isLoading={batchesQuery.isPending}
            overlayText={"No batch jobs"}
          />
        </div>
      </div>
    );
  };

  return (
    <div>
      {isLoading && (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-14.5 w-full" />
          <Skeleton className="h-144 w-full" />
        </div>
      )}

      {isSuccess && batchesQuery.data.length == 0 && (
        <PermissionsWrapper
          keys={batchPermissionKeys}
          messages={{
            [PermissionsReasons.UPGRADE_REQUIRED]: (
              <AccessControlModal
                title="Subscribe to access Batch"
                children={NoSubNoHistoryContent()}
                imageLight={batchLight}
                imageDark={batchDark}
              />
            ),
          }}
          fallbackToChildren={[PermissionsReasons.ROLE_DENY]}
        >
          {batchContent()}
        </PermissionsWrapper>
      )}

      {isSuccess && batchesQuery.data.length > 0 && batchContent()}
    </div>
  );
};
